import type z from "zod/v4";
import { type CreatorSignalsFor, createConditionEngine, type FieldDefinition } from "../../utils/conditionEngine";

export type { ComparisonOperator, ConditionValue, Operator, TagOperator } from "../../utils/conditionEngine";

// ──────────────────────────────────────────────────────────────────────────
// Field registry
// ──────────────────────────────────────────────────────────────────────────

export const FIELD_DEFINITIONS = {
  criticalCount: {
    kind: "numeric",
    label: "Critical Violations",
    tooltip: "Number of critical-severity safety violations flagged on this creator's content.",
    signalKey: "safetyCriticalCount",
  },
  highCount: {
    kind: "numeric",
    label: "High Violations",
    tooltip: "Number of high-severity safety violations flagged on this creator's content.",
    signalKey: "safetyHighCount",
  },
  mediumCount: {
    kind: "numeric",
    label: "Medium Violations",
    tooltip: "Number of medium-severity safety violations flagged on this creator's content.",
    signalKey: "safetyMediumCount",
  },
  lowCount: {
    kind: "numeric",
    label: "Low Violations",
    tooltip: "Number of low-severity safety violations flagged on this creator's content.",
    signalKey: "safetyLowCount",
  },
  ghostedCount: {
    kind: "numeric",
    label: "Ghosted Samples",
    tooltip: "A creator who received product samples but hasn't posted any content for your brand.",
  },
  sampleToPostPct: {
    kind: "numeric",
    label: "Sample-to-Post Rate",
    tooltip: "The percentage of received samples that resulted in a posted video.",
  },
  followerCount: {
    kind: "numeric",
    label: "Followers",
    tooltip: "The creator's current follower count.",
  },
  gmvAmount: {
    kind: "numeric",
    label: "GMV (90d)",
    tooltip: "Total GMV this creator has generated for your brand in the last 90 days.",
  },
  cohortId: {
    kind: "tag",
    label: "Cohort",
    tooltip: "Whether the creator belongs to a specific saved cohort.",
  },
} as const satisfies Record<string, FieldDefinition>;

export type FieldId = keyof typeof FIELD_DEFINITIONS;

const engine = createConditionEngine(FIELD_DEFINITIONS, { version: 2 });

export const ruleConditionsSchema = engine.ruleConditionsSchema;
export const matchesConditions = engine.matchesConditions;
export const referencesField = engine.referencesField;

export type Condition = z.infer<typeof engine.conditionSchema>;
export type ConditionGroup = z.infer<typeof engine.conditionGroupSchema>;
export type RuleConditions = z.infer<typeof engine.ruleConditionsSchema>;
export type CreatorSignals = CreatorSignalsFor<typeof FIELD_DEFINITIONS>;
